// Is the current user a platform admin? (member of platform_admins)
//
// platform_admins has a self-read RLS policy (mig 195: USING user_id =
// auth.uid()), so a user can read their OWN row and nobody else's — a
// non-admin's query simply returns nothing → false. Cached for the session.
//
// This is a UI convenience gate only — the real enforcement is server-side
// (RLS / edge-function platform-admin checks). Don't rely on it for security.
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

function usePlatformAdminQuery() {
  return useQuery({
    queryKey: ['is-platform-admin'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      // getSession() not getUser() — getUser acquires the auth Web-Lock and
      // can race with token refreshes (see project_auth_direct_rest_bypass).
      const { data: { session } } = await supabase.auth.getSession()
      const uid = session?.user?.id
      if (!uid) return false
      const { data, error } = await supabase
        .from('platform_admins')
        .select('user_id')
        .eq('user_id', uid)
        .maybeSingle()
      if (error) return false
      return !!data
    },
  })
}

export function useIsPlatformAdmin(): boolean {
  return usePlatformAdminQuery().data ?? false
}

/**
 * Same check, but exposes the pending flag so a gate can render a spinner
 * on first load instead of flashing "Platform admins only" (the query
 * resolves `false` while in-flight — a real admin briefly sees the denial).
 * Use via <RequirePlatformAdmin>, not raw, so the flash fix is uniform.
 */
export function useIsPlatformAdminState(): { isAdmin: boolean; isPending: boolean } {
  const q = usePlatformAdminQuery()
  return { isAdmin: q.data ?? false, isPending: q.isPending }
}

/**
 * The same question for a decision that must fail CLOSED — the AI-assistant
 * consent page (/oauth/consent), where "is a platform admin" REFUSES. The two
 * hooks above read a failed lookup as false, which is right for a UI gate that
 * grants (a non-admin sees nothing extra) and wrong for one that refuses: here
 * a failed or session-less read is 'unknown', never 'not_admin'. Its own cache
 * entry, so the lenient one above is untouched.
 */
export function usePlatformAdminVerdict(): 'admin' | 'not_admin' | 'unknown' | 'pending' {
  const q = useQuery({
    queryKey: ['is-platform-admin', 'fail-closed'],
    staleTime: 5 * 60_000,
    retry: 1,
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession()
      const uid = session?.user?.id
      if (!uid) throw new Error('no session to check')
      const { data, error } = await supabase
        .from('platform_admins')
        .select('user_id')
        .eq('user_id', uid)
        .maybeSingle()
      if (error) throw error
      return !!data
    },
  })
  if (q.data !== undefined) return q.data ? 'admin' : 'not_admin'
  return q.isError ? 'unknown' : 'pending'
}
